import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Car, 
  Calendar, 
  Palette, 
  Fuel, 
  Gauge, 
  Settings2, 
  Key,
  Share2,
  Star
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ImageGallery } from '@/components/ImageGallery';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { api, formatPrice, formatWhatsAppLink } from '@/lib/api';
import { toast } from 'sonner';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function VehicleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const data = await api.getVehicle(slug);
        setVehicle(data);
        // SEO: Update page title and meta tags
        document.title = `${data.titulo} - ${formatPrice(data.precio)} | Chocados GDL`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', `${data.titulo} ${data.marca} ${data.modelo} ${data.color} - ${formatPrice(data.precio)}. ${data.condicion || ''} en Chocados GDL Guadalajara.`);
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) ogTitle.setAttribute('content', `${data.titulo} - ${formatPrice(data.precio)}`);
        const ogDesc = document.querySelector('meta[property="og:description"]');
        if (ogDesc) ogDesc.setAttribute('content', `${data.marca} ${data.modelo} ${data.color} - ${data.condicion || 'Disponible'} en Chocados GDL`);
        const ogImage = document.querySelector('meta[property="og:image"]');
        if (ogImage && data.imagenes?.length > 0) {
          const imgUrl = data.imagenes[0].startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${data.imagenes[0]}` : data.imagenes[0];
          ogImage.setAttribute('content', imgUrl);
        }
      } catch (error) {
        console.error('Error fetching vehicle:', error);
        toast.error('Error al cargar el vehículo');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVehicle();
    return () => {
      document.title = 'Chocados GDL | Autos Siniestrados y Seminuevos en Guadalajara';
    };
  }, [slug, navigate]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: vehicle.titulo,
          text: `Mira este vehículo: ${vehicle.titulo} - ${formatPrice(vehicle.precio)}`,
          url
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!vehicle) {
    return null;
  }

  const whatsappMessage = `¡Hola!, me interesa cotizar el vehículo: ${vehicle.titulo}`;

  const specs = [
    { icon: Car, label: 'Tipo', value: vehicle.tipo },
    { icon: Calendar, label: 'Año', value: vehicle.modelo },
    { icon: Palette, label: 'Color', value: vehicle.color },
    { icon: Settings2, label: 'Marca', value: vehicle.marca },
    vehicle.transmision && { icon: Settings2, label: 'Transmisión', value: vehicle.transmision },
    vehicle.kilometraje && { icon: Gauge, label: 'Kilometraje', value: `${vehicle.kilometraje.toLocaleString()} km` },
    vehicle.combustible && { icon: Fuel, label: 'Combustible', value: vehicle.combustible },
    vehicle.numero_llave && { icon: Key, label: 'No. Llave', value: vehicle.numero_llave },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Back Button */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Volver al inventario</span>
          </Link>

          <article className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" itemScope itemType="https://schema.org/Vehicle">
            <meta itemProp="name" content={vehicle.titulo} />
            <meta itemProp="brand" content={vehicle.marca} />
            <meta itemProp="color" content={vehicle.color} />
            <meta itemProp="modelDate" content={String(vehicle.modelo)} />
            {/* Gallery - Left Side */}
            <div className="lg:col-span-7 xl:col-span-8">
              <ImageGallery images={vehicle.imagenes} videos={vehicle.videos} title={vehicle.titulo} />
            </div>

            {/* Info Panel - Right Side */}
            <div className="lg:col-span-5 xl:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-6">
                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  {vehicle.destacado && (
                    <Badge className="bg-primary/20 text-primary border-primary/30">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      Destacado
                    </Badge>
                  )}
                  {vehicle.condicion && (
                    <Badge variant="secondary" className="bg-secondary/20 text-secondary border-secondary/30">
                      {vehicle.condicion}
                    </Badge>
                  )}
                </div>

                {/* Internal ID */}
                {vehicle.id_interno && (
                  <p className="text-sm font-semibold text-primary uppercase tracking-wider" data-testid="vehicle-id-interno">
                    ID: {vehicle.id_interno}
                  </p>
                )}

                {/* Title */}
                <h1 className="text-2xl md:text-3xl font-bold text-foreground leading-tight" data-testid="vehicle-title">
                  {vehicle.titulo}
                </h1>

                {/* Price */}
                <div className="bg-card border border-border rounded-sm p-4" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                  <p className="text-sm text-muted-foreground mb-1">Precio</p>
                  <p className="text-3xl md:text-4xl font-bold text-primary" data-testid="vehicle-price" itemProp="price">
                    {formatPrice(vehicle.precio)}
                  </p>
                  <meta itemProp="priceCurrency" content="MXN" />
                </div>

                {/* WhatsApp Buttons */}
                {vehicle.whatsapp_numbers?.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      Contactar por WhatsApp
                    </p>
                    <div className="grid gap-2">
                      {vehicle.whatsapp_numbers.map((wa, index) => (
                        <a
                          key={index}
                          href={formatWhatsAppLink(wa.number, whatsappMessage)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whatsapp-btn justify-center"
                          data-testid={`whatsapp-btn-${index}`}
                        >
                          <WhatsAppIcon className="w-5 h-5" />
                          <span>{wa.number}</span>
                          {wa.label && (
                            <span className="text-white/70 text-sm">({wa.label})</span>
                          )}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <Separator className="bg-border" />

                {/* Specs */}
                <div className="space-y-4">
                  <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Especificaciones
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {specs.map((spec, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                          <spec.icon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{spec.label}</p>
                          <p className="text-sm font-medium text-foreground">{spec.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description */}
                {vehicle.descripcion && (
                  <>
                    <Separator className="bg-border" />
                    <div className="space-y-3">
                      <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                        Descripción
                      </p>
                      <p className="text-muted-foreground whitespace-pre-line" data-testid="vehicle-description">
                        {vehicle.descripcion}
                      </p>
                    </div>
                  </>
                )}

                {/* Share Button */}
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="w-full border-border hover:border-primary"
                  data-testid="share-button"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartir
                </Button>
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
